import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { cloudinaryOptimize } from '../utils/cloudinaryOptimize';
import './PropertiesPage.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FILTER_KEY = 'hss_properties_filters';
const SORT_KEY = 'hss_properties_sort';

const DEFAULT_FILTERS = {
  city: '', postcode: '', states: [], state: '', property_id: '',
  property_type: '', total_bedrooms: '', min_bathrooms: '',
  min_rent: '', max_rent: '', rent_type: '',
  single_storey: false, furnished: false, internet_wifi: false,
  close_to_transport: false, close_to_shops: false, close_to_doctor: false,
  has_ac: false, ceiling_fans: false, security_systems: false,
  solar_power: false, solar_hws: false,
  wheelchair_access: false, garden: false, parking_avail: false, pets: false,
  radius: '',
  bedrooms_or_more: false,
};

const PropertiesPage = () => {
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);

  // Restore sort + filters from sessionStorage so returning from a property doesn't reset them.
  // A ?state=&city= URL param (e.g. from the "View properties in {suburb}" link on Best Locations)
  // takes priority over the stored filters.
  const [sortBy, setSortBy] = useState(() => sessionStorage.getItem(SORT_KEY) || 'newest');
  const [filters, setFilters] = useState(() => {
    let base;
    try { base = JSON.parse(sessionStorage.getItem(FILTER_KEY)) || DEFAULT_FILTERS; }
    catch { base = DEFAULT_FILTERS; }
    const params = new URLSearchParams(window.location.search);
    const urlState = params.get('state');
    const urlCity = params.get('city');
    if (urlState || urlCity) {
      return { ...DEFAULT_FILTERS, ...(urlState ? { state: urlState, states: [] } : {}), ...(urlCity ? { city: urlCity } : {}) };
    }
    return base;
  });

  // Persist to sessionStorage on every change
  useEffect(() => { sessionStorage.setItem(FILTER_KEY, JSON.stringify(filters)); }, [filters]);
  useEffect(() => { sessionStorage.setItem(SORT_KEY, sortBy); }, [sortBy]);

  // Nearby postcodes for radius search
  const [nearbyPostcodes, setNearbyPostcodes] = useState([]);
  useEffect(() => {
    if (filters.city && filters.radius) {
      axios.get(`${API}/postcodes/nearby`, { params: { suburb: filters.city, radius_km: filters.radius } })
        .then(r => setNearbyPostcodes(r.data.found ? r.data.postcodes : []))
        .catch(() => setNearbyPostcodes([]));
    } else {
      setNearbyPostcodes([]);
    }
  }, [filters.city, filters.radius]); // eslint-disable-line react-hooks/exhaustive-deps

  const amenitiesOptions = [
    'Parking', 'Garden', 'Internet/WiFi', 'Furnished', 'Pet friendly',
    'Wheelchair accessible', 'Close to public transport', 'Close to shops'
  ];

  const fetchCurrentUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setCurrentUser(response.data);
    } catch (error) {
      // not authenticated
    }
  }, []);

  const fetchProperties = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/properties`);
      setProperties(response.data);
      setFilteredProperties(response.data);
    } catch (error) {
      console.error('Failed to fetch properties:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCurrentUser();
    fetchProperties();
  }, [fetchCurrentUser, fetchProperties]);

  const applyFilters = () => {
    let filtered = [...properties];

    filtered = filtered.filter(p => p.available_bedrooms !== 0);

    if (filters.city) {
      if (filters.radius && nearbyPostcodes.length > 0) {
        // Radius search: include properties in nearby postcodes
        filtered = filtered.filter(p =>
          p.city.toLowerCase().includes(filters.city.toLowerCase()) ||
          nearbyPostcodes.includes(p.postcode)
        );
      } else {
        filtered = filtered.filter(p => p.city.toLowerCase().includes(filters.city.toLowerCase()));
      }
    }
    if (filters.postcode) {
      const postcodes = filters.postcode.split(',').map(p => p.trim()).filter(Boolean);
      filtered = filtered.filter(p => postcodes.some(pc => (p.postcode || '').startsWith(pc)));
    }
    if (filters.property_id) {
      const pid = filters.property_id.toLowerCase().trim();
      filtered = filtered.filter(p => (p.property_id || '').toLowerCase().includes(pid));
    }
    if (filters.states && filters.states.length > 0) {
      filtered = filtered.filter(p => filters.states.includes(p.state));
    } else if (filters.state) {
      filtered = filtered.filter(p => p.state === filters.state);
    }
    if (filters.property_type) {
      filtered = filtered.filter(p => p.property_type === filters.property_type);
    }
    if (filters.total_bedrooms) {
      const beds = parseInt(filters.total_bedrooms);
      filtered = filters.bedrooms_or_more ? filtered.filter(p => (p.total_bedrooms || 0) >= beds) : filtered.filter(p => p.total_bedrooms === beds);
    }
    const getRent = (p) => {
      const r = p.weekly_rent_per_person || 0;
      if (filters.rent_type === 'singles') return r - Math.min(73.23, Math.max(0, 0.75*(r-74.10)));
      if (filters.rent_type === 'couples') return r - Math.min(103.40, Math.max(0, 0.75*(r-123.10)));
      return r;
    };
    if (filters.min_rent) filtered = filtered.filter(p => getRent(p) >= parseFloat(filters.min_rent));
    if (filters.max_rent) filtered = filtered.filter(p => getRent(p) <= parseFloat(filters.max_rent));
    if (filters.min_bathrooms) filtered = filtered.filter(p => (p.total_bathrooms || 0) >= parseInt(filters.min_bathrooms));
    if (filters.single_storey) filtered = filtered.filter(p => p.single_storey);
    if (filters.furnished) filtered = filtered.filter(p => p.furnished);
    if (filters.internet_wifi) filtered = filtered.filter(p => p.amenities?.includes('Internet/WiFi'));
    if (filters.close_to_transport) filtered = filtered.filter(p => p.amenities?.includes('Close to public transport'));
    if (filters.close_to_shops) filtered = filtered.filter(p => p.amenities?.includes('Close to shops'));
    if (filters.close_to_doctor) filtered = filtered.filter(p => p.close_to_doctor);
    if (filters.has_ac) filtered = filtered.filter(p => p.has_ac);
    if (filters.ceiling_fans) filtered = filtered.filter(p => p.ceiling_fans);
    if (filters.security_systems) filtered = filtered.filter(p => p.security_systems);
    if (filters.solar_power) filtered = filtered.filter(p => p.solar_power);
    if (filters.solar_hws) filtered = filtered.filter(p => p.solar_hws);
    if (filters.wheelchair_access) filtered = filtered.filter(p => p.wheelchair_access);
    if (filters.garden) filtered = filtered.filter(p => p.amenities?.includes('Garden'));
    if (filters.parking_avail) filtered = filtered.filter(p => p.has_parking || (p.parking_spaces || 0) >= 1);
    if (filters.pets) filtered = filtered.filter(p => p.pets_allowed);

    // Sort — always put photo-bearing properties first as a secondary sort key
    const hasPhoto = p => (p.images && p.images.length > 0) ? 0 : 1;
    filtered.sort((a, b) => {
      let primary = 0;
      switch (sortBy) {
        case 'newest': primary = new Date(b.created_at) - new Date(a.created_at); break;
        case 'oldest': primary = new Date(a.created_at) - new Date(b.created_at); break;
        case 'rent_low': primary = a.weekly_rent_per_person - b.weekly_rent_per_person; break;
        case 'rent_high': primary = b.weekly_rent_per_person - a.weekly_rent_per_person; break;
        case 'state': primary = (a.state || '').localeCompare(b.state || ''); break;
        case 'suburb': primary = (a.city || '').localeCompare(b.city || ''); break;
        default: primary = 0;
      }
      // Secondary: photos first
      return primary !== 0 ? primary : hasPhoto(a) - hasPhoto(b);
    });

    setFilteredProperties(filtered);
  };

  useEffect(() => {
    applyFilters();
  }, [filters, properties, sortBy, nearbyPostcodes]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFilterChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      setFilters(prev => ({ ...prev, [name]: checked }));
    } else {
      setFilters(prev => ({ ...prev, [name]: value }));
    }
  };

  const clearFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setSortBy('newest');
    sessionStorage.removeItem(FILTER_KEY);
    sessionStorage.removeItem(SORT_KEY);
  };

  return (
    <div className="properties-page">
      <div className="properties-header">
        <div className="container">
          <div className="header-content">
            <div>
              <h1>Available Properties</h1>
              <p>Find your ideal shared housing</p>
            </div>
            <div className="header-actions">
              {currentUser && (
                <button className="btn btn-primary" onClick={() => navigate('/properties/add')}>
                  + Add Property
                </button>
              )}
              <button className="btn btn-secondary" onClick={() => navigate('/dashboard')}>
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="container">
        <div className="properties-layout">
          {/* Filters Sidebar */}
          <div className="filters-sidebar">
            <div className="filters-header">
              <h2>Filter &amp; Sort</h2>
              <button className="clear-filters-btn" onClick={clearFilters}>Clear All</button>
            </div>

            <button className="btn btn-primary" onClick={() => window.scrollTo(0,0)} data-testid="search-btn" style={{ width: '100%', fontSize: '15px', padding: '10px 16px', fontWeight: '700', marginBottom: '8px' }}>Search</button>

            <div className="filter-group">
              <label className="filter-label">Sort By</label>
              <select value={sortBy} onChange={e => setSortBy(e.target.value)} className="filter-input" data-testid="sort-select">
                <option value="newest">Newest Listed</option>
                <option value="oldest">Oldest Listed</option>
                <option value="rent_low">Rent: Low to High</option>
                <option value="rent_high">Rent: High to Low</option>
                <option value="state">State (A–Z)</option>
                <option value="suburb">Suburb (A–Z)</option>
              </select>
            </div>

            <div style={{ background: '#fee2e2', color: '#991b1b', fontWeight: '700', fontSize: '12px', padding: '5px 10px', borderRadius: '6px', margin: '8px 0 4px', letterSpacing: '0.04em' }}>Search for:</div>

            <div className="filter-group">
              <label className="filter-label">Property ID</label>
              <input type="text" name="property_id" value={filters.property_id} onChange={handleFilterChange} placeholder="e.g., prop_abc123" className="filter-input" autoComplete="off" data-testid="filter-property-id" />
            </div>

            <div style={{ background: '#fee2e2', color: '#991b1b', fontWeight: '700', fontSize: '12px', padding: '5px 10px', borderRadius: '6px', margin: '8px 0 4px', letterSpacing: '0.04em' }}>Select Filters :</div>

            <div className="filter-group">
              <label className="filter-label">Suburb / City</label>
              <input type="text" name="city" value={filters.city} onChange={handleFilterChange} placeholder="e.g., Sydney" className="filter-input" autoComplete="off" />
              <div style={{ marginTop: '6px' }}>
                <label className="filter-label" style={{ fontSize: '11px', color: '#6b7280' }}>incl. surrounding areas</label>
                <select name="radius" value={filters.radius} onChange={handleFilterChange} className="filter-input" style={{ fontSize: '12px' }} data-testid="radius-select" disabled={!filters.city}>
                  <option value="">Exact match only</option>
                  <option value="5">... 5km radius</option>
                  <option value="10">... 10km radius</option>
                  <option value="25">... 25km radius</option>
                  <option value="50">... 50km radius</option>
                </select>
                {filters.city && filters.radius && nearbyPostcodes.length > 0 && (
                  <div style={{ fontSize: '11px', color: '#059669', marginTop: '4px' }}>
                    {nearbyPostcodes.length} postcodes within {filters.radius}km
                  </div>
                )}
              </div>
            </div>

            <div className="filter-group">
              <label className="filter-label">Postcode <span style={{ fontSize: '10px', color: '#9ca3af' }}>(comma-separated for multiple)</span></label>
              <input type="text" name="postcode" value={filters.postcode} onChange={handleFilterChange} placeholder="e.g., 4000, 4001" className="filter-input" autoComplete="off" data-testid="filter-postcode" />
            </div>

            <div className="filter-group">
              <label className="filter-label">State <span style={{ fontSize: '10px', color: '#9ca3af' }}>(select multiple)</span></label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>
                {['NSW','VIC','QLD','SA','WA','TAS','NT','ACT'].map(s => (
                  <label key={s} style={{ display: 'flex', alignItems: 'center', gap: '3px', cursor: 'pointer', fontSize: '12px', padding: '3px 8px', borderRadius: '12px', border: '1px solid', borderColor: filters.states.includes(s) ? '#2563eb' : '#d1d5db', background: filters.states.includes(s) ? '#eff6ff' : 'white', color: filters.states.includes(s) ? '#2563eb' : '#374151', fontWeight: filters.states.includes(s) ? '700' : '400' }}>
                    <input type="checkbox" style={{ display: 'none' }} checked={filters.states.includes(s)}
                      onChange={e => setFilters(prev => ({ ...prev, states: e.target.checked ? [...prev.states, s] : prev.states.filter(x => x !== s) }))} />
                    {s}
                  </label>
                ))}
              </div>
            </div>

            <div className="filter-group">
              <label className="filter-label">Property Type</label>
              <select name="property_type" value={filters.property_type} onChange={handleFilterChange} className="filter-input">
                <option value="">All Types</option>
                <option value="house">House</option>
                <option value="apartment">Apartment</option>
                <option value="townhouse">Townhouse</option>
                <option value="unit">Unit</option>
              </select>
            </div>

            <div className="filter-group">
              <label className="filter-label">Total Number of Bedrooms</label>
              <select name="total_bedrooms" value={filters.total_bedrooms} onChange={handleFilterChange} className="filter-input">
                <option value="">Any</option>
                <option value="1">1 Bedroom</option>
                <option value="2">2 Bedrooms</option>
                <option value="3">3 Bedrooms</option>
                <option value="4">4 Bedrooms</option>
                <option value="5">5 Bedrooms</option>
                <option value="6">6 Bedrooms</option>
                <option value="7">7+ Bedrooms</option>
              </select>
              <label className="checkbox-label-small"><input type="checkbox" checked={filters.bedrooms_or_more} onChange={e => setFilters(prev => ({ ...prev, bedrooms_or_more: e.target.checked }))} data-testid="bedrooms-or-more" /><span>or more (e.g. 4+)</span></label>
            </div>

            <div className="filter-group">
              <label className="filter-label">Min Bathrooms</label>
              <select name="min_bathrooms" value={filters.min_bathrooms} onChange={handleFilterChange} className="filter-input">
                <option value="">Any</option>
                <option value="1">1+</option>
                <option value="2">2+</option>
                <option value="3">3+</option>
              </select>
            </div>

            <div className="filter-group">
              <label className="filter-label">Parking</label>
              <select name="parking" value={filters.parking} onChange={handleFilterChange} className="filter-input">
                <option value="">Any</option>
                <option value="any">Has Parking</option>
                <option value="garage">Garage/Carport</option>
              </select>
            </div>


            <div className="filter-group">
              <label className="filter-label">Rent Filter By</label>
              <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                {[['singles', 'Singles'], ['couples', 'Couples']].map(([val, label]) => (
                  <label key={val} style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', fontSize: '12px', padding: '3px 8px', borderRadius: '12px', border: '1px solid', borderColor: filters.rent_type === val ? '#2563eb' : '#d1d5db', background: filters.rent_type === val ? '#eff6ff' : 'white', color: filters.rent_type === val ? '#2563eb' : '#374151', fontWeight: filters.rent_type === val ? '700' : '400' }}>
                    <input type="radio" style={{ display: 'none' }} checked={filters.rent_type === val} onChange={() => setFilters(prev => ({ ...prev, rent_type: prev.rent_type === val ? '' : val }))} />
                    {label}
                  </label>
                ))}
              </div>
            </div>

            <div className="filter-group">
              <label className="filter-label" style={{ whiteSpace: 'nowrap' }}>Min Rent per bedroom ($/wk)</label>
              {filters.rent_type && <div style={{ fontSize: '11px', background: '#fce7f3', color: '#9d174d', padding: '2px 6px', borderRadius: '4px', marginBottom: '4px' }}>(after CRA Pensioner Subsidy deducted)</div>}
              <input type="number" name="min_rent" value={filters.min_rent} onChange={handleFilterChange} placeholder="$" className="filter-input" data-testid="filter-min-rent" />
            </div>

            <div className="filter-group">
              <label className="filter-label" style={{ whiteSpace: 'nowrap' }}>Max Rent per bedroom ($/wk)</label>
              {filters.rent_type && <div style={{ fontSize: '11px', background: '#fce7f3', color: '#9d174d', padding: '2px 6px', borderRadius: '4px', marginBottom: '4px' }}>(after CRA Pensioner Subsidy deducted)</div>}
              <input type="number" name="max_rent" value={filters.max_rent} onChange={handleFilterChange} placeholder="$" className="filter-input" />
            </div>

            <div className="filter-group">
              <label className="filter-label">Property Features</label>
              <div className="amenities-filters">
                {[
                  { key: 'single_storey',     label: 'Single Storey' },
                  { key: 'furnished',          label: 'Furnished' },
                  { key: 'internet_wifi',      label: 'Internet/WiFi' },
                  { key: 'close_to_transport', label: 'Close to public transport' },
                  { key: 'close_to_shops',     label: 'Close to shops' },
                  { key: 'close_to_doctor',    label: 'Close to Doctor/Hospital' },
                  { key: 'has_ac',             label: 'Air Conditioning' },
                  { key: 'ceiling_fans',       label: 'Ceiling Fans' },
                  { key: 'security_systems',   label: 'Security Systems' },
                  { key: 'solar_power',        label: 'Solar Power' },
                  { key: 'solar_hws',          label: 'Solar Hot Water' },
                  { key: 'wheelchair_access',  label: 'Wheelchair Accessible' },
                  { key: 'garden',             label: 'Garden' },
                  { key: 'parking_avail',      label: 'Parking' },
                  { key: 'pets',               label: 'Pets Allowed' },
                ].map(({ key, label }) => (
                  <label key={key} className="checkbox-label-small">
                    <input type="checkbox" checked={!!filters[key]} onChange={e => setFilters(prev => ({ ...prev, [key]: e.target.checked }))} />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Properties Grid */}
          <div className="properties-content">
            <div className="results-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <p>{filteredProperties.length} properties found</p>
            </div>

            {loading ? (
              <div className="loading">Loading properties...</div>
            ) : filteredProperties.length === 0 ? (
              <div className="no-results">
                <p>No properties found matching your criteria.</p>
                <button className="btn btn-secondary" onClick={clearFilters}>Clear Filters</button>
              </div>
            ) : (
              <div className="properties-grid">
                {filteredProperties.map(property => (
                  <div key={property.property_id} className="property-card">
                    <div className="property-image">
                      {property.images && property.images.length > 0 ? (
                        <img src={cloudinaryOptimize(property.images[0], 600)} alt={property.address} />
                      ) : (
                        <div className="placeholder-image">No Image</div>
                      )}
                    </div>
                    <div className="property-details">
                      <h3 className="property-type">{property.property_type || 'Property'}</h3>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <p className="property-address" style={{ margin: 0 }}>{property.city} {property.state} {property.postcode || ''}</p>
                        <div style={{ display: 'flex', gap: '4px', flexShrink: 0, marginLeft: '8px' }}>
                          <button className="btn btn-primary" onClick={() => navigate(`/properties/${property.property_id}`)} data-testid={`view-property-${property.property_id}`} style={{ padding: '5px 12px', background: '#2563eb', color: 'white', borderRadius: '6px', fontWeight: '700', fontSize: '12px', border: 'none' }}>View</button>
                          {currentUser && (currentUser.user_id === property.added_by_user_id || currentUser.role === 'admin') && (
                            <button className="btn" onClick={() => navigate(`/properties/${property.property_id}/edit`)} data-testid={`edit-property-${property.property_id}`} style={{ padding: '5px 12px', background: '#f3f4f6', color: '#374151', borderRadius: '6px', fontWeight: '700', fontSize: '12px', border: '1px solid #d1d5db' }}>Edit</button>
                          )}
                        </div>
                      </div>
                      {property.weekly_rent_per_person > 0 && property.total_bedrooms > 0 && <div style={{ fontSize: '13px', color: '#4b5563', fontWeight: '700' }}>Gross Rent: ${(Math.round(property.weekly_rent_per_person) * property.total_bedrooms).toFixed(0)}/week ({property.total_bedrooms} bedrooms)</div>}
                      {property.weekly_rent_per_person === 0 && property.total_rent > 0 && <div style={{ fontSize: '13px', color: '#4b5563', fontWeight: '700' }}>Gross Rent: ${Math.round(property.total_rent)}/week</div>}
                      <div className="property-info">
                        <span>🛏 {property.available_bedrooms || '?'} available{property.total_bedrooms ? ` / ${property.total_bedrooms} total` : ''}</span>
                        <span>🚿 {property.total_bathrooms > 0 ? `${property.total_bathrooms} bath` : 'bath TBA'}</span>
                      </div>
                      <div className="property-amenities">
                        {property.amenities?.slice(0, 3).map(amenity => (
                          <span key={amenity} className="amenity-tag">{amenity}</span>
                        ))}
                        {property.amenities?.length > 3 && (
                          <span className="amenity-tag">+{property.amenities.length - 3} more</span>
                        )}
                      </div>
                      <div className="property-footer">
                        <div className="property-price">
                          {property.weekly_rent_per_person > 0 ? (
                            <>
                              <span className="price">${Math.round(property.weekly_rent_per_person)}</span>
                              <span className="period">p/bedroom</span>
                              <span className="period">p/week</span>
                            </>
                          ) : property.total_rent > 0 ? (
                            <>
                              <span className="price">${Math.round(property.total_rent)}</span>
                              <span className="period">/ week total rent</span>
                            </>
                          ) : (
                            <span className="period">Price on application</span>
                          )}
                        </div>
                        <div style={{ marginTop: '6px' }}>
                          {property.weekly_rent_per_person > 0 && (() => {
                            const r = property.weekly_rent_per_person;
                            const sCRA = Math.min(73.23, Math.max(0, 0.75 * (r - 74.10)));
                            const cCRA = Math.min(103.40, Math.max(0, 0.75 * (r - 123.10)));
                            return (
                              <div style={{ color: '#1a2332', fontWeight: '700' }}>
                                <div style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '4px', color: '#6b7280' }}>After CRA:</div>
                                <div style={{ lineHeight: '1.3', color: '#b91c1c', whiteSpace: 'nowrap' }}><span style={{ fontSize: '18px', textTransform: 'uppercase', fontWeight: '700' }}>Singles</span> <span style={{ fontSize: '24px', fontWeight: '800' }}>${Math.round(r - sCRA)}/wk</span></div>
                                <div style={{ lineHeight: '1.3', color: '#b91c1c', whiteSpace: 'nowrap' }}><span style={{ fontSize: '18px', textTransform: 'uppercase', fontWeight: '700' }}>Couples</span> <span style={{ fontSize: '24px', fontWeight: '800' }}>${Math.round(r - cCRA)}/wk</span></div>
                              </div>
                            );
                          })()}
                        </div>
                      </div>

                      {/* Agent contact — full width below footer */}
                      <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '2px solid #e5e7eb' }}>
                        {property.agency_name && (
                          <div style={{ fontSize: '13px', fontWeight: '700', color: '#1a2332', marginBottom: '4px' }}>
                            {property.agency_logo && <img src={property.agency_logo} alt={property.agency_name} style={{ height: '22px', objectFit: 'contain', verticalAlign: 'middle', marginRight: '6px', background: 'white', borderRadius: '3px', padding: '2px' }} onError={e => e.target.style.display='none'} />}
                            {property.agency_name}
                          </div>
                        )}
                        <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '6px' }}>
                          via <strong style={{ color: '#1a2332' }}>{property.listing_source_name || 'Rent.com.au'}</strong>
                        </div>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                          {property.domain_url ? (
                            <a href={property.domain_url} target="_blank" rel="noopener noreferrer"
                              style={{ padding: '6px 14px', background: '#dc2626', color: 'white', borderRadius: '5px', fontWeight: '800', fontSize: '13px', textDecoration: 'none' }}
                              data-testid={`contact-agent-${property.property_id}`}>
                              Contact Agent →
                            </a>
                          ) : (
                            <span style={{ padding: '6px 14px', background: '#e5e7eb', color: '#6b7280', borderRadius: '5px', fontWeight: '700', fontSize: '13px' }}
                              data-testid={`contact-agent-unavailable-${property.property_id}`}>
                              Listing link unavailable
                            </span>
                          )}
                          <div style={{ fontSize: '11px', color: '#9ca3af' }}>
                            {property.listing_date && <span>{new Date(property.listing_date).toLocaleDateString('en-AU', { day:'numeric', month:'short', year:'numeric' })} · </span>}
                            <span>ID: {property.property_id?.slice(-8)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertiesPage;